import { useState, useMemo } from 'react';
import './RaffleGeneratorPage.css';

interface RaffleForm {
  amount: string;
  date: string;
  time: string;
  description: string;
  channels: string;
  language: 'ru' | 'en' | 'uk';
}

const translations = {
  ru: {
    title: 'Розыгрыш',
    prize: 'Приз',
    date: 'Дата',
    participants: 'Участников',
    creator: 'Создатель',
    joinButton: 'Участвовать',
    cancelButton: 'Отменить',
    requiredSubscription: 'Требуется подписка',
  },
  en: {
    title: 'Raffle',
    prize: 'Prize',
    date: 'Date',
    participants: 'Participants',
    creator: 'Creator',
    joinButton: 'Join',
    cancelButton: 'Cancel',
    requiredSubscription: 'Subscription required',
  },
  uk: {
    title: 'Розіграш',
    prize: 'Приз',
    date: 'Дата',
    participants: 'Учасників',
    creator: 'Творець',
    joinButton: 'Брати участь',
    cancelButton: 'Скасувати',
    requiredSubscription: 'Потрібна підписка',
  },
};

export default function RaffleGeneratorPage() {
  const [form, setForm] = useState<RaffleForm>({
    amount: '100',
    date: '',
    time: '18:00',
    description: '',
    channels: '',
    language: 'ru',
  });

  const [copied, setCopied] = useState(false);

  // Генерация команды
  const generatedCommand = useMemo(() => {
    const parts = ['@plzpaymebot raffle'];

    if (form.amount) {
      parts.push(form.amount);
    }

    if (form.date) {
      // Конвертируем из YYYY-MM-DD в DD.MM.YYYY
      const [year, month, day] = form.date.split('-');
      parts.push(`${day}.${month}.${year}`);
    }

    if (form.time) {
      parts.push(form.time);
    }

    if (form.description.trim()) {
      parts.push(`"${form.description.trim()}"`);
    }

    if (form.channels.trim()) {
      // Разбиваем каналы и добавляем @ если нет
      const channelList = form.channels
        .split(/[,\s]+/)
        .filter(c => c.trim())
        .map(c => c.startsWith('@') ? c : `@${c}`)
        .join(' ');
      if (channelList) {
        parts.push(channelList);
      }
    }

    return parts.join(' ');
  }, [form]);

  // Форматирование даты для превью
  const formattedDate = useMemo(() => {
    if (!form.date) return '—';
    const [year, month, day] = form.date.split('-');
    return `${day}.${month}.${year} ${form.time || ''}`.trim();
  }, [form.date, form.time]);

  // Список каналов для превью
  const channelList = useMemo(() => {
    if (!form.channels.trim()) return [];
    return form.channels
      .split(/[,\s]+/)
      .filter(c => c.trim())
      .map(c => c.startsWith('@') ? c : `@${c}`);
  }, [form.channels]);

  const t = translations[form.language];

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(generatedCommand);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const handleChange = (field: keyof RaffleForm, value: string) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  // Минимальная дата - завтра
  const minDate = useMemo(() => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    return tomorrow.toISOString().split('T')[0];
  }, []);

  return (
    <div className="raffle-generator-page">
      <div className="raffle-generator-container">
        <h1>🎰 Генератор розыгрышей</h1>
        <p className="subtitle">Создайте команду для розыгрыша и вставьте её в любой Telegram чат</p>

        <div className="raffle-generator-content">
          {/* Форма */}
          <div className="raffle-form">
            <div className="form-group">
              <label>💰 Сумма приза (USDT)</label>
              <input
                type="number"
                min="5"
                max="10000"
                value={form.amount}
                onChange={(e) => handleChange('amount', e.target.value)}
                placeholder="100"
              />
              <span className="hint">Минимум 5 USDT</span>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>📅 Дата розыгрыша</label>
                <input
                  type="date"
                  min={minDate}
                  value={form.date}
                  onChange={(e) => handleChange('date', e.target.value)}
                />
              </div>

              <div className="form-group">
                <label>🕐 Время</label>
                <input
                  type="time"
                  value={form.time}
                  onChange={(e) => handleChange('time', e.target.value)}
                />
              </div>
            </div>

            <div className="form-group">
              <label>📝 Описание (опционально)</label>
              <textarea
                value={form.description}
                onChange={(e) => handleChange('description', e.target.value)}
                placeholder="Розыгрыш подарка! Подписывайтесь на канал..."
                rows={3}
              />
            </div>

            <div className="form-group">
              <label>📢 Обязательные подписки (опционально)</label>
              <input
                type="text"
                value={form.channels}
                onChange={(e) => handleChange('channels', e.target.value)}
                placeholder="@channel1, @channel2"
              />
              <span className="hint">Бот должен быть админом этих каналов</span>
            </div>

            <div className="form-group">
              <label>🌐 Язык сообщений</label>
              <select
                value={form.language}
                onChange={(e) => handleChange('language', e.target.value as 'ru' | 'en' | 'uk')}
              >
                <option value="ru">🇷🇺 Русский</option>
                <option value="en">🇬🇧 English</option>
                <option value="uk">🇺🇦 Українська</option>
              </select>
            </div>

            {/* Сгенерированная команда */}
            <div className="generated-command">
              <label>📋 Команда для Telegram:</label>
              <div className="command-box">
                <code>{generatedCommand}</code>
                <button
                  className={`copy-btn ${copied ? 'copied' : ''}`}
                  onClick={handleCopy}
                >
                  {copied ? '✓ Скопировано' : '📋 Копировать'}
                </button>
              </div>
            </div>

            <div className="instructions">
              <h4>Как использовать:</h4>
              <ol>
                <li>Скопируйте команду выше</li>
                <li>Откройте любой Telegram чат или канал</li>
                <li>Вставьте команду в поле ввода</li>
                <li>Выберите появившийся результат</li>
                <li>Розыгрыш будет создан!</li>
              </ol>
            </div>
          </div>

          {/* Превью */}
          <div className="raffle-preview">
            <h3>Предпросмотр сообщения</h3>
            <div className="telegram-message">
              <div className="message-content">
                <div className="raffle-title">🎰 {t.title}</div>

                <div className="raffle-info">
                  <div className="info-row">
                    <span className="icon">💰</span>
                    <span className="label">{t.prize}:</span>
                    <span className="value">{form.amount || '—'} USDT</span>
                  </div>

                  <div className="info-row">
                    <span className="icon">📅</span>
                    <span className="label">{t.date}:</span>
                    <span className="value">{formattedDate}</span>
                  </div>

                  <div className="info-row">
                    <span className="icon">👥</span>
                    <span className="label">{t.participants}:</span>
                    <span className="value">0</span>
                  </div>

                  <div className="info-row">
                    <span className="icon">🎯</span>
                    <span className="label">{t.creator}:</span>
                    <span className="value">@you</span>
                  </div>
                </div>

                {form.description && (
                  <div className="raffle-description">
                    📝 {form.description}
                  </div>
                )}

                {channelList.length > 0 && (
                  <div className="required-channels">
                    ⚠️ {t.requiredSubscription}: {channelList.join(', ')}
                  </div>
                )}

                <div className="raffle-buttons">
                  <button className="join-btn">🎯 {t.joinButton} (0)</button>
                  <button className="cancel-btn">❌ {t.cancelButton}</button>
                </div>
              </div>
            </div>

            <div className="fee-info">
              <h4>💵 Комиссия</h4>
              <div className="fee-row">
                <span>Сервис (1%):</span>
                <span>{(Number(form.amount) * 0.01).toFixed(2)} USDT</span>
              </div>
              <div className="fee-row">
                <span>Сеть (~4 TRX):</span>
                <span>~0.60 USDT</span>
              </div>
              <div className="fee-row total">
                <span>Итого к списанию:</span>
                <span>{(Number(form.amount) * 1.01 + 0.6).toFixed(2)} USDT</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
